import { Injectable, NgZone, TemplateRef, inject } from '@angular/core';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { updateParamsObject } from '../../shared/utils/filter-utils';
import { Subscription } from 'rxjs';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../store/app/app.selectors';

@Injectable({
  providedIn: 'root',
})
export class OffcanvasService {
  readonly ngbOffcanvas = inject(NgbOffcanvas);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly ngZone = inject(NgZone);
  readonly store = inject(Store);

  private offcanvasRef?: NgbOffcanvasRef;
  readonly subscriptions = new Subscription();

  open(content: TemplateRef<HTMLTemplateElement>): void {
    // Manually control body overflow with custom classes to fix the fist fight between modals and offcanvas (known issue)
    document.body.classList.remove('offcanvas-closed');
    document.body.classList.add('offcanvas-open');

    this.offcanvasRef = this.ngbOffcanvas.open(content, { position: 'end', backdrop: 'static' });
    this.removeGhostDuplicateHtml();
  }

  close(returnUrl?: string, params?: HttpParams) {
    this.offcanvasRef?.close();
    // Manually control body overflow with custom classes to fix the fist fight between modals and offcanvas (known issue)
    document.body.classList.remove('offcanvas-open');
    document.body.classList.add('offcanvas-closed');
    if (returnUrl) {
      this.offCanvasNavigate(returnUrl, params);
    }
  }

  offCanvasNavigate(url: string, params?: HttpParams): void {
    // Check if user has navigation access - block navigation to offcanvas routes if not
    const hasAccess = this.store.selectSnapshot(AppSelectors.hasNavigationAccess);

    if (!hasAccess) {
      // Don't navigate to offcanvas routes when user doesn't have navigation access
      console.warn('Offcanvas navigation blocked: User does not have navigation access');
      return;
    }

    let queryParams = new HttpParams();
    if (params) {
      queryParams = params;
    } else {
      queryParams = queryParams.appendAll(this.route.snapshot.queryParams);
    }
    // this needed to load the parent table with correct params results after opening child view
    this.ngZone.run(() => {
      this.router.navigate(['/'], { skipLocationChange: true }).then(() => {
        this.router.navigate([url], { queryParams: updateParamsObject(queryParams) });
        this.removeGhostDuplicateHtml();
      });
    });
  }

  closeAfterNavigationEnd() {
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.close();
        }
      }),
    );
  }

  // Offcanvas creates a duplicate HTML block of the offcanvas content which creates issues with duplicated form id's and breaks some default html behaviour.
  // removing the duplicated block solves a lot of these issues.
  removeGhostDuplicateHtml() {
    const offCanvasGhostContainer = document.body.querySelector('#offCanvasGhostContainer');
    if (offCanvasGhostContainer) offCanvasGhostContainer.remove();
  }
}
